/**
 * Creator program (affiliates) page: applications, approved creators, owed
 * amounts, mark-as-paid, manual Shopify sync. Pairs with the public
 * application form at /creators/apply.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  BadgeDollarSign, Check, Copy, ExternalLink, HandCoins, Link2, Pause, Play,
  RefreshCw, Sparkles, UserPlus, X,
} from "lucide-react";
import { Badge, Button, EmptyState, Input, Label, Select, Skeleton, useConfirm, useToast } from "@/components/ui";
import { formatDate, formatRelative } from "@/lib/format";
import { affiliatesApi, type Affiliate } from "./api";
import { AffiliateDetailDrawer, type DetailAction } from "./AffiliateDetailDrawer";
import { AddDirectModal } from "./AddDirectModal";

/** USD formatter - the backend returns plain dollar amounts. */
function usd(n: number | string | null | undefined): string {
  const v = Number(n ?? 0);
  return v.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

const STATUS_COLORS: Record<Affiliate["status"], string> = {
  pending: "amber",
  approved: "emerald",
  rejected: "zinc",
  suspended: "red",
};

export function AffiliatesPage() {
  const [rows, setRows] = useState<Affiliate[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Default tab: Creators - unless there are pending applications, in which
  // case Applications takes over (set once data arrives, see load below).
  const [tab, setTab] = useState<"applications" | "creators" | "ambassadors">("creators");
  const [syncing, setSyncing] = useState(false);
  const [syncNote, setSyncNote] = useState<string | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [detailAction, setDetailAction] = useState<DetailAction>(null);
  const [addOpen, setAddOpen] = useState(false);

  const toast = useToast();
  const confirm = useConfirm();

  // Once the user picks a tab themselves, the auto-default stops overriding them.
  const tabTouched = useRef(false);

  const load = useCallback(async () => {
    try {
      const data = await affiliatesApi.list();
      setRows(data.affiliates);
      setLoadError(null);
      setTab((current) =>
        tabTouched.current ? current : data.affiliates.some((r) => r.status === "pending") ? "applications" : "creators",
      );
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : "Failed to load creators");
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  /** Pick a tab by hand - marks it so the auto-default no longer overrides. */
  function selectTab(next: "applications" | "creators" | "ambassadors") {
    tabTouched.current = true;
    setTab(next);
  }

  const pending = useMemo(() => rows?.filter((r) => r.status === "pending") ?? [], [rows]);
  const creators = useMemo(() => rows?.filter((r) => r.status !== "pending" && r.status !== "rejected" && r.type !== "ambassador") ?? [], [rows]);
  const ambassadors = useMemo(() => rows?.filter((r) => r.status !== "pending" && r.status !== "rejected" && r.type === "ambassador") ?? [], [rows]);
  const rejectedCount = rows?.filter((r) => r.status === "rejected").length ?? 0;

  const totalOwed = (rows ?? []).reduce((a, r) => a + (Number(r.earned) - Number(r.paid)), 0);
  const totalEarned = (rows ?? []).reduce((a, r) => a + Number(r.earned), 0);
  const activeCreators = rows?.filter((r) => r.status === "approved").length ?? 0;
  const totalSales = (rows ?? []).reduce((a, r) => a + Number(r.sales_count), 0);

  async function runSync() {
    setSyncing(true);
    try {
      const res = await affiliatesApi.sync();
      const s = res.summary;
      setSyncNote(s.inserted
        ? `Found ${s.scanned} orders - ${s.inserted} new sale${s.inserted === 1 ? "" : "s"} credited`
        : "No new affiliate sales found");
      await load();
      toast.success("Sync complete");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Sync failed");
    } finally {
      setSyncing(false);
    }
  }

  function openApproval(a: Affiliate) {
    setDetailAction("approve");
    setDetailId(a.id);
  }

  async function quickReject(a: Affiliate) {
    const ok = await confirm({
      title: `Reject application from ${a.first_name} ${a.last_name}?`,
      description: "They keep their record and can apply again later.",
      confirmLabel: "Reject",
      danger: true,
    });
    if (!ok) return;
    try {
      await affiliatesApi.reject(a.id);
      toast.success("Application rejected");
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to reject");
    }
  }

  const list = tab === "applications" ? pending : tab === "ambassadors" ? ambassadors : creators;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-zinc-100">Creators</h1>
          <p className="text-sm text-zinc-500">
            Affiliate program - sales tracked automatically from Shopify discount codes.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => setAddOpen(true)}>
            <UserPlus className="h-4 w-4" /> Add directly
          </Button>
          <a href="/creators/apply" target="_blank" rel="noreferrer" className="inline-flex">
            <Button variant="outline" size="sm">
              <ExternalLink className="h-4 w-4" /> Application form
            </Button>
          </a>
          <Button size="sm" onClick={runSync} disabled={syncing}>
            <RefreshCw className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Syncing..." : "Sync Shopify now"}
          </Button>
        </div>
      </header>

      {syncNote && (
        <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-2 text-sm text-emerald-300">{syncNote}</div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <SummaryCard icon={<HandCoins className="h-4 w-4" />} label="Owed to creators" value={usd(totalOwed)} highlight />
        <SummaryCard icon={<BadgeDollarSign className="h-4 w-4" />} label="Total commissions earned" value={usd(totalEarned)} />
        <SummaryCard icon={<Sparkles className="h-4 w-4" />} label="Active creators" value={String(activeCreators)} />
        <SummaryCard icon={<Link2 className="h-4 w-4" />} label="Tracked sales" value={String(totalSales)} />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-zinc-800/70">
        <TabButton active={tab === "applications"} onClick={() => selectTab("applications")} count={pending.length}>
          Applications
        </TabButton>
        <TabButton active={tab === "creators"} onClick={() => selectTab("creators")} count={creators.length}>
          Creators
        </TabButton>
        <TabButton active={tab === "ambassadors"} onClick={() => selectTab("ambassadors")} count={ambassadors.length}>
          Ambassadors
        </TabButton>
      </div>

      {rows === null && !loadError ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-14 w-full" />)}
        </div>
      ) : loadError ? (
        <EmptyState icon={X} title="Couldn't load creators" description={loadError} action={<Button onClick={load}>Try again</Button>} />
      ) : list.length === 0 ? (
        tab === "applications" ? (
          <EmptyState
            icon={UserPlus}
            title="No pending applications"
            description={rejectedCount
              ? "The queue is clear. Rejected applications can re-apply from the public form."
              : "Share the application form link with creators who want to join the program."}
            action={<a href="/creators/apply" target="_blank" rel="noreferrer"><Button variant="outline" size="sm">Open application form</Button></a>}
          />
        ) : (
          <EmptyState
            icon={Sparkles}
            title="No creators yet"
            description="Approve an application and their discount code is created automatically."
          />
        )
      ) : (
        <div className="overflow-hidden rounded-xl border border-zinc-800/70 bg-zinc-900/30">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-800/70 bg-zinc-900/40 text-left text-xs uppercase tracking-wide text-zinc-500">
                <th className="px-4 py-2.5 font-medium">Creator</th>
                <th className="px-4 py-2.5 font-medium">Code</th>
                {tab === "creators" && <th className="px-4 py-2.5 font-medium">Earned / Paid / Owed</th>}
                <th className="px-4 py-2.5 font-medium">Sales</th>
                <th className="px-4 py-2.5 font-medium">{tab === "applications" ? "Applied" : "Status"}</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {list.map((a) => (
                <tr key={a.id} className="border-b border-zinc-800/40 last:border-0 hover:bg-zinc-900/40">
                  <td className="px-4 py-3">
                    <button className="text-left" onClick={() => { setDetailAction(null); setDetailId(a.id); }}>
                      <div className="font-medium text-zinc-100 hover:underline">{a.first_name} {a.last_name}</div>
                      <div className="text-xs text-zinc-500">{a.email}</div>
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    {a.discount_code ? <CodeChip code={a.discount_code} /> : <span className="text-xs text-zinc-400">not created yet</span>}
                  </td>
                  {tab === "creators" && (
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="text-zinc-500">{usd(a.earned)} / {usd(a.paid)} / </span>
                      <span className="font-semibold text-zinc-100">{usd(Number(a.earned) - Number(a.paid))}</span>
                    </td>
                  )}
                  <td className="px-4 py-3 text-zinc-300">{a.sales_count}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {tab === "applications" ? (
                      <span className="text-xs text-zinc-500" title={formatDate(a.created_at)}>{formatRelative(a.created_at)}</span>
                    ) : (
                      <Badge color={STATUS_COLORS[a.status]}>{a.status === "suspended" ? "suspended" : "active"}</Badge>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {a.status === "pending" ? (
                      <div className="flex justify-end gap-1.5">
                        <Button size="sm" variant="outline" onClick={() => quickReject(a)}>Reject</Button>
                        <Button size="sm" onClick={() => openApproval(a)}>Review &amp; approve</Button>
                      </div>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => { setDetailAction(null); setDetailId(a.id); }}>Details</Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {detailId && (
        <AffiliateDetailDrawer
          id={detailId}
          initialAction={detailAction}
          onClose={() => { setDetailId(null); setDetailAction(null); }}
          onChanged={load}
        />
      )}

      {addOpen && (
        <AddDirectModal
          defaultType={tab === "ambassadors" ? "ambassador" : "creator"}
          onClose={() => setAddOpen(false)}
          onAdded={async (a) => {
            await load();
            setDetailAction(null);
            setDetailId(a.id);
          }}
        />
      )}
    </div>
  );
}

function SummaryCard({ icon, label, value, highlight }: { icon: React.ReactNode; label: string; value: string; highlight?: boolean }) {
  return (
    <div className={`rounded-xl border p-4 ${highlight ? "border-emerald-500/30 bg-emerald-500/10" : "border-zinc-800/70 bg-zinc-900/30"}`}>
      <div className="flex items-center gap-2 text-xs text-zinc-500">
        {icon} {label}
      </div>
      <div className={`mt-1 text-2xl font-semibold ${highlight ? "text-emerald-300" : "text-zinc-100"}`}>{value}</div>
    </div>
  );
}

function TabButton({ active, onClick, count, children }: { active: boolean; onClick: () => void; count: number; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
        active ? "border-zinc-100 text-zinc-100" : "border-transparent text-zinc-500 hover:text-zinc-300"
      }`}
    >
      {children}
      <span className={`ml-1.5 rounded-full px-1.5 py-0.5 text-xs ${active ? "bg-zinc-100 text-zinc-900" : "bg-zinc-800 text-zinc-400"}`}>{count}</span>
    </button>
  );
}

/** Discount code with a one-click copy button. */
export function CodeChip({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard unavailable */ }
  }
  return (
    <button onClick={copy} className="inline-flex items-center gap-1.5 rounded-md border border-zinc-700 bg-zinc-800/60 px-2 py-0.5 font-mono text-xs font-medium text-zinc-200 hover:border-zinc-600" title="Click to copy">
      {code}
      {copied ? <Check className="h-3 w-3 text-emerald-400" /> : <Copy className="h-3 w-3 text-zinc-400" />}
    </button>
  );
}
